/* Phase 36 Batch AS — RunIdea Homepage V2.
 *
 * Repositions RunIdea from "AI Deal Analyzer" → "Complete AI-Powered
 * Real Estate Investing Platform". Ten sections, white background,
 * indigo-600 accents, slate-800 text, generous whitespace, Stripe /
 * Linear visual reference.
 *
 * BF.AUDIT D — legacy V1 landing (Landing.legacy.jsx) was deleted in
 * Phase 44 Batch BF.AUDIT after sitting unused since Batch AS.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Bell,
  Calculator,
  CheckCircle2,
  Compass,
  DollarSign,
  FileBarChart,
  Globe2,
  LineChart,
  MapPin,
  PieChart,
  Search,
  Sparkles,
  Star,
  Telescope,
  TrendingUp,
  User as UserIcon,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import {
  Cell,
  Pie,
  PieChart as RechartsPie,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import PromoTicker from "@/components/landing/PromoTicker";
import WaitlistModal from "@/components/landing/WaitlistModal";
import discoveryPreview from "@/assets/discovery-preview.jpg";
import portfolioPreview from "@/assets/portfolio-preview.jpg";
import {
  trackLandingV2Viewed,
  trackLandingCtaClicked,
  trackPricingCardHovered,
} from "@/lib/landingEvents";

// Batch AT — sessionStorage key for the captured referral code. Mirrors
// the constant in `WaitlistModal.jsx` so both surfaces speak the same
// language for cross-tab persistence within a visit.
const REF_STORAGE_KEY = "runidea_ref_code";

// Reads ?ref=<code> from the current URL (preferred), falling back to
// any previously captured code in this tab's sessionStorage. Normalised
// to lowercase so "MANUEL", "Manuel" and "manuel" attribute together.
function _readReferralCode() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get("ref") || "").trim().toLowerCase().slice(0, 120);
    if (fromUrl) {
      try {
        window.sessionStorage?.setItem(REF_STORAGE_KEY, fromUrl);
      } catch { /* private mode */ }
      return fromUrl;
    }
    const stored = window.sessionStorage?.getItem(REF_STORAGE_KEY);
    return stored ? stored.trim().toLowerCase().slice(0, 120) : null;
  } catch {
    return null;
  }
}

// ─── Tiny presentational helpers ───────────────────────────────────────────
function SectionTitle({ kicker, title, subtitle, center = true }) {
  return (
    <div className={`mx-auto max-w-3xl ${center ? "text-center" : ""}`}>
      {kicker && (
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">
          {kicker}
        </p>
      )}
      <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base text-slate-600 sm:text-lg">{subtitle}</p>
      )}
    </div>
  );
}

function FeatureCard({ Icon, title, description, className = "" }) {
  return (
    <div
      className={`group rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md ${className}`}
    >
      <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 transition-colors group-hover:bg-indigo-100">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600">{description}</p>
    </div>
  );
}

// ─── Batch AT — Referral banner ────────────────────────────────────────────
// Subtle strip rendered above the hero whenever a `?ref=<code>` query
// param is detected (or persisted from an earlier visit in the same
// tab). Indigo accent, no animation, no dismiss button — disappears
// naturally when the user navigates away without ?ref=.
function ReferralBanner({ code }) {
  if (!code) return null;
  return (
    <div
      className="border-b border-indigo-100 bg-indigo-50/70"
      data-testid="referral-banner"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-2 text-xs font-medium text-indigo-700 sm:px-6 sm:text-sm">
        <span aria-hidden="true">👋</span>
        <span>
          Invited by <span className="font-semibold">{code}</span>
          {" — get priority access"}
        </span>
      </div>
    </div>
  );
}

function TopNav() {
  return (
    <nav className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/80 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
        <Link
          to="/"
          className="flex items-center gap-2"
          data-testid="landing-logo"
        >
          <img src="/logo.png" alt="RunIdea" className="h-8 w-8 rounded-lg" />
          <span className="text-sm font-semibold tracking-tight text-slate-900">
            RunIdea.ai
          </span>
        </Link>
        <div className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
          <a
            href="#features"
            className="transition-colors hover:text-slate-900"
            data-testid="nav-features"
          >
            Features
          </a>
          <a
            href="#how"
            className="transition-colors hover:text-slate-900"
            data-testid="nav-how"
          >
            How it works
          </a>
          <a
            href="#pricing"
            className="transition-colors hover:text-slate-900"
            data-testid="nav-pricing"
          >
            Pricing
          </a>
          <Link
            to="/login"
            className="transition-colors hover:text-slate-900"
            data-testid="nav-login"
          >
            Log in
          </Link>
        </div>
        <Link to="/signup">
          <Button
            size="sm"
            className="bg-indigo-600 text-white hover:bg-indigo-700"
            data-testid="nav-cta"
          >
            Start free
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </nav>
  );
}

// ─── 1. Hero ────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-white via-indigo-50/30 to-white"
      data-testid="landing-hero"
    >
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:py-28">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
            <Sparkles className="h-3.5 w-3.5" />
            AI-powered real estate investing
          </span>
          <h1 className="mt-5 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Find, Analyze, Fund and Manage Real Estate Investments
            <span className="block text-indigo-600">Worldwide.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
            Discover opportunities, analyze deals, collaborate with investors,
            manage properties and grow your portfolio from one platform.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="h-12 w-full bg-indigo-600 px-6 text-white shadow-sm hover:bg-indigo-700 sm:w-auto"
                data-testid="hero-cta-start-free"
                onClick={() =>
                  trackLandingCtaClicked({ cta_name: "start_free", section: "hero" })
                }
              >
                Start Free
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/signup?next=/discovery" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="h-12 w-full border-slate-300 bg-white px-6 text-slate-800 hover:bg-slate-50 sm:w-auto"
                data-testid="hero-cta-discover"
                onClick={() =>
                  trackLandingCtaClicked({ cta_name: "discover_deals", section: "hero" })
                }
              >
                Discover Deals
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-500">
            No credit card · 3 deals free forever · ~2-min setup
          </p>
        </div>

        {/* Hero illustration — Discovery preview screenshot with a subtle frame */}
        <div className="relative flex items-center justify-center">
          <div className="absolute -left-8 -top-8 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-12 -right-4 h-64 w-64 rounded-full bg-emerald-200/40 blur-3xl" aria-hidden="true" />
          <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <img
              src={discoveryPreview}
              alt="RunIdea Discovery — find investment properties"
              className="block w-full"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 2. Discovery ──────────────────────────────────────────────────────────
function DiscoverySection() {
  const features = [
    { Icon: Search, title: "Saved Searches", desc: "Filter by city, cap rate, price, beds, cash flow." },
    { Icon: Bell, title: "Daily Opportunity Alerts", desc: "Email digest of new matches every morning." },
    { Icon: TrendingUp, title: "Cap Rate Signals", desc: "Pre-computed yields per listing." },
    { Icon: Wallet, title: "Cash Flow Forecasts", desc: "See $X/mo cash flow before you click." },
    { Icon: Sparkles, title: "AI Deal Scoring", desc: "0–100 score across every signal." },
    { Icon: Zap, title: "Strategy Matching", desc: "Tag deals as flip, BRRRR, rental, hold." },
  ];
  return (
    <section className="bg-white py-20" data-testid="landing-discovery">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Discovery"
          title="AI-Powered Property Discovery"
          subtitle="Stop hunting on Zillow. Let AI surface deals matching your criteria — automatically."
        />
        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
            <img
              src={discoveryPreview}
              alt="RunIdea Discovery interface"
              className="block w-full"
              loading="lazy"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {features.map((f) => (
              <FeatureCard
                key={f.title}
                Icon={f.Icon}
                title={f.title}
                description={f.desc}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 3. How it works ───────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { Icon: Compass, title: "Discover", desc: "AI finds deals matching your criteria." },
    { Icon: Calculator, title: "Analyze", desc: "Underwriting + risk score + counter-offer suggestion." },
    { Icon: Users, title: "Fund", desc: "Partner with co-investors, track contributions." },
    { Icon: BarChart3, title: "Manage", desc: "Portfolio dashboard + tenant management." },
    { Icon: TrendingUp, title: "Exit", desc: "Sell, refinance, or 1031 with AI guidance." },
  ];
  return (
    <section
      id="how"
      className="border-y border-slate-200 bg-slate-50/60 py-20"
      data-testid="landing-how"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle kicker="How it works" title="From listing to exit — one platform" />
        <ol className="mt-14 grid gap-6 lg:grid-cols-5">
          {steps.map((s, i) => (
            <li
              key={s.title}
              className="relative flex flex-col items-start rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              data-testid={`landing-how-step-${i + 1}`}
            >
              <div className="absolute -top-3 left-5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-semibold text-white">
                {i + 1}
              </div>
              <div className="mt-2 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <s.Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ─── 4. Feature categories ─────────────────────────────────────────────────
function FeatureCategories() {
  const cats = [
    {
      Icon: Search,
      label: "Find",
      tone: "text-indigo-600 bg-indigo-50",
      items: ["Discovery Engine", "Market Scanner", "Opportunity Alerts"],
    },
    {
      Icon: Calculator,
      label: "Analyze",
      tone: "text-emerald-600 bg-emerald-50",
      items: ["Rental Calculator", "BRRRR Calculator", "Flip Calculator", "AI Deal Analyzer"],
    },
    {
      Icon: Users,
      label: "Fund",
      tone: "text-amber-600 bg-amber-50",
      items: ["Partnership Management", "Ownership Tracking", "Capital Contributions", "Profit Distributions"],
    },
    {
      Icon: BarChart3,
      label: "Manage",
      tone: "text-rose-600 bg-rose-50",
      items: ["Portfolio Intelligence", "Financial Reporting", "Property Tracking", "Performance Dashboards"],
    },
  ];
  return (
    <section
      id="features"
      className="bg-white py-20"
      data-testid="landing-feature-categories"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Everything you need"
          title="Built for the full investing lifecycle"
          subtitle="From your first deal to your hundredth — one platform, end-to-end."
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {cats.map((c) => (
            <div
              key={c.label}
              className="flex flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              data-testid={`feature-cat-${c.label.toLowerCase()}`}
            >
              <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-lg ${c.tone}`}>
                <c.Icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">{c.label}</h3>
              <ul className="mt-3 space-y-2">
                {c.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 5. Global investing ────────────────────────────────────────────────────
function GlobalSection() {
  const features = [
    { Icon: DollarSign, title: "Multi-Currency Support", desc: "USD, EUR, GBP, COP, MXN, BRL + 240 more." },
    { Icon: TrendingUp, title: "FX Risk Scoring", desc: "Per-country risk score with live Frankfurter rates." },
    { Icon: LineChart, title: "Exchange Rate Tracking", desc: "Historical FX charts on every deal." },
    { Icon: MapPin, title: "International Property Analysis", desc: "Same underwriting, native currency." },
  ];
  return (
    <section
      className="relative overflow-hidden border-y border-slate-200 bg-slate-900 py-20 text-white"
      data-testid="landing-global"
    >
      <div className="absolute inset-0 opacity-[0.07]" aria-hidden="true">
        <Globe2 className="absolute -right-24 -top-24 h-[28rem] w-[28rem] text-white" strokeWidth={0.6} />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">
            Global
          </p>
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Invest Anywhere. Without the Currency Confusion.
          </h2>
          <p className="mt-4 text-base text-slate-300 sm:text-lg">
            RunIdea supports 243 countries and tracks FX risk in real-time.
          </p>
        </div>
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur"
              data-testid={`global-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/20 text-indigo-300">
                <f.Icon className="h-5 w-5" />
              </div>
              <h3 className="text-sm font-semibold">{f.title}</h3>
              <p className="mt-1.5 text-sm text-slate-300">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 6. Partnership investing ──────────────────────────────────────────────
const PARTNERS = [
  { name: "Manuel (GP)", value: 40, color: "#4f46e5" },
  { name: "Carla", value: 25, color: "#06b6d4" },
  { name: "Diego", value: 20, color: "#10b981" },
  { name: "Sarah", value: 15, color: "#f59e0b" },
];

function PartnershipSection() {
  const features = [
    { Icon: Users, title: "Multiple Investors", desc: "Unlimited partners per deal." },
    { Icon: PieChart, title: "Automatic Ownership Percentages", desc: "Computed from capital contributions." },
    { Icon: Wallet, title: "Capital Tracking", desc: "Per-contributor ledger." },
    { Icon: DollarSign, title: "Profit Distribution Automation", desc: "Waterfall + preferred returns built in." },
  ];
  return (
    <section className="bg-white py-20" data-testid="landing-partnership">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Co-investing"
          title="Invest Together. Track Every Share Automatically."
          subtitle="Bring co-investors into deals with clean ownership splits and automated distributions."
        />
        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
          {/* Donut chart */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sample deal · $480k acquisition
            </p>
            <div className="mt-3 h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={PARTNERS}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {PARTNERS.map((p) => (
                      <Cell key={p.name} fill={p.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(v, n) => [`${v}%`, n]}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-xs">
              {PARTNERS.map((p) => (
                <li key={p.name} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />
                  <span className="text-slate-700">{p.name}</span>
                  <span className="ml-auto font-semibold text-slate-900">{p.value}%</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {features.map((f) => (
              <FeatureCard
                key={f.title}
                Icon={f.Icon}
                title={f.title}
                description={f.desc}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── 7. Portfolio intelligence ─────────────────────────────────────────────
function PortfolioSection() {
  return (
    <section
      className="border-y border-slate-200 bg-slate-50/60 py-20"
      data-testid="landing-portfolio"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Portfolio"
          title="See Your Portfolio Like an Institution."
          subtitle="KPIs across all your deals, with year-over-year tracking and verdict distribution."
        />
        <div className="mt-12 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
          <img
            src={portfolioPreview}
            alt="RunIdea Portfolio Intelligence dashboard"
            className="block w-full"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

// ─── 8. Testimonials ───────────────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote:
      "I stopped wasting hours on Zillow. The Discovery engine surfaces 3-4 real deals every week that match my exact 8% cap target.",
    name: "Sarah K.",
    role: "Texas portfolio · 8 SFRs",
    color: "bg-indigo-500",
  },
  {
    quote:
      "AI now suggests offers below asking based on my target return, not just a 10% discount. It's like having a senior analyst on speed dial.",
    name: "Marcus T.",
    role: "Flip investor · Atlanta",
    color: "bg-emerald-500",
  },
  {
    quote:
      "Partnership tracking saved me 4 hours per week. Capital splits, profit distributions, all automatic.",
    name: "Carla R.",
    role: "Syndication GP · 22 deals",
    color: "bg-amber-500",
  },
  {
    quote:
      "Multi-currency works flawlessly for my Bogotá rentals. FX risk score gave me an exit signal I'd have missed.",
    name: "Diego P.",
    role: "International investor · 12 properties",
    color: "bg-rose-500",
  },
];

function Testimonials() {
  return (
    <section className="bg-white py-20" data-testid="landing-testimonials">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Social proof"
          title="What Early Investors Are Saying"
        />
        <p className="mt-3 text-center text-xs text-slate-500">
          Quotes from RunIdea alpha testers · Names are real, photos representational
        </p>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              data-testid={`testimonial-${t.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
            >
              <div className="flex items-center gap-1 text-amber-400">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-current" />
                ))}
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-slate-700">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-4">
                <div className={`flex h-9 w-9 items-center justify-center rounded-full text-white ${t.color}`}>
                  <UserIcon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="truncate text-xs text-slate-500">{t.role}</p>
                </div>
                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                  Alpha
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── 9. Pricing ────────────────────────────────────────────────────────────
function PricingSection({ onJoinWaitlist }) {
  const [foundersCount, setFoundersCount] = useState({ count: 0, cap: 200 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/waitlist/founders/count");
        if (!cancelled) setFoundersCount(res.data);
      } catch {
        /* server unreachable — leave defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tiers = [
    {
      key: "free",
      name: "Free",
      price: "$0",
      cadence: "forever",
      features: [
        "3 deals analyzed / month",
        "Rental + Flip + BRRRR calculators",
        "Basic risk scoring",
      ],
      missing: ["Discovery engine", "Comps + Market Validation", "AI Copilot"],
      cta: { label: "Start Free", to: "/signup", testid: "pricing-free-cta", action: "start_free" },
      tone: "border-slate-200",
    },
    {
      key: "pro_monthly",
      name: "Pro",
      price: "$29",
      cadence: "/month · $290/yr (save $58)",
      features: [
        "Unlimited deals",
        "Property Discovery + Daily Digest",
        "Comps + Market Validation",
        "AI Negotiation Engine",
        "Document Analysis",
        "Portfolio Intelligence",
      ],
      cta: { label: "Join Pro Waitlist", waitlist: "pro_monthly", testid: "pricing-pro-cta" },
      badge: "Recommended",
      tone: "border-indigo-300 ring-2 ring-indigo-100",
    },
    {
      key: "founders_lifetime",
      name: "Founders Lifetime",
      price: "$499",
      cadence: "one-time · first 200 customers",
      features: [
        "All Pro features forever",
        "No monthly fee, ever",
        "Lifetime feature updates",
        "Direct line to founder",
      ],
      cta: {
        label: "Reserve Founders Spot",
        waitlist: "founders_lifetime",
        testid: "pricing-founders-cta",
      },
      badge: "Limited",
      tone: "border-amber-300",
    },
  ];

  const remaining = Math.max(0, foundersCount.cap - foundersCount.count);

  return (
    <section
      id="pricing"
      className="border-y border-slate-200 bg-slate-50/60 py-20"
      data-testid="landing-pricing"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          kicker="Pricing"
          title="Simple Pricing. Real Power."
          subtitle="Free for analysts. Paid plans launch Q1 2027 — reserve your spot; your monthly price locks in at signup."
        />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {tiers.map((t) => (
            <div
              key={t.key}
              onMouseEnter={() => trackPricingCardHovered({ plan: t.key })}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-all hover:shadow-md ${t.tone}`}
              data-testid={`pricing-card-${t.key}`}
            >
              {t.badge && (
                <span
                  className={`absolute -top-3 right-4 inline-flex items-center rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                    t.badge === "Recommended"
                      ? "bg-indigo-600 text-white"
                      : "bg-amber-500 text-white"
                  }`}
                >
                  {t.badge}
                </span>
              )}
              <h3 className="text-lg font-semibold text-slate-900">{t.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-semibold tracking-tight text-slate-900">{t.price}</span>
                <span className="text-sm text-slate-500">{t.cadence}</span>
              </div>
              <ul className="mt-5 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                    {f}
                  </li>
                ))}
                {(t.missing || []).map((m) => (
                  <li key={m} className="flex items-start gap-2 text-sm text-slate-400">
                    <span className="mt-0.5 inline-block h-4 w-4 flex-shrink-0 rounded-full border border-slate-300" />
                    {m}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {t.cta.to ? (
                  <Link to={t.cta.to}>
                    <Button
                      size="lg"
                      variant={t.badge === "Recommended" ? "default" : "outline"}
                      data-testid={t.cta.testid}
                      className={
                        t.badge === "Recommended"
                          ? "w-full bg-indigo-600 text-white hover:bg-indigo-700"
                          : "w-full"
                      }
                      onClick={() =>
                        trackLandingCtaClicked({
                          cta_name: t.cta.action || "start_free",
                          section: "pricing",
                        })
                      }
                    >
                      {t.cta.label}
                    </Button>
                  </Link>
                ) : (
                  <Button
                    size="lg"
                    data-testid={t.cta.testid}
                    onClick={() => onJoinWaitlist(t.cta.waitlist)}
                    className={
                      t.badge === "Recommended"
                        ? "w-full bg-indigo-600 text-white hover:bg-indigo-700"
                        : t.badge === "Limited"
                          ? "w-full bg-amber-500 text-white hover:bg-amber-600"
                          : "w-full"
                    }
                  >
                    {t.cta.label}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Founders counter */}
        <p
          className="mt-6 text-center text-sm text-slate-600"
          data-testid="pricing-founders-counter"
        >
          <span className="font-semibold text-amber-600">{foundersCount.count}</span>{" "}
          / {foundersCount.cap} founders spots reserved · {remaining} remaining
        </p>
      </div>
    </section>
  );
}

// ─── 10. Final CTA ─────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section
      className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 py-20 text-white"
      data-testid="landing-final-cta"
    >
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
          Ready to Build Wealth Through Real Estate?
        </h2>
        <p className="mt-4 text-base text-indigo-100 sm:text-lg">
          Discover, Analyze, Fund and Manage Real Estate Investments in One Platform.
        </p>
        <div className="mt-8 flex flex-col items-center">
          <Link to="/signup">
            <Button
              size="lg"
              className="h-12 bg-white px-8 text-indigo-700 shadow-lg hover:bg-slate-50"
              data-testid="final-cta-start-free"
              onClick={() =>
                trackLandingCtaClicked({ cta_name: "start_free", section: "final" })
              }
            >
              Start Free
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-indigo-200">
            No credit card required · 3 deals free forever
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white py-10">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 text-xs text-slate-500 sm:flex-row sm:justify-between sm:px-6">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="RunIdea" className="h-6 w-6 rounded" />
          <span className="font-medium text-slate-700">RunIdea.ai</span>
          <span className="text-slate-400">© {new Date().getFullYear()}</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-slate-800">Privacy</Link>
          <Link to="/terms" className="hover:text-slate-800">Terms</Link>
          <a href="mailto:hello@runidea.ai" className="hover:text-slate-800">Contact</a>
        </div>
      </div>
    </footer>
  );
}

// ─── Page export ───────────────────────────────────────────────────────────
export default function Landing() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistPlan, setWaitlistPlan] = useState("pro_monthly");
  // Lazy initializer — fires once on mount, captures `?ref=` straight
  // from the URL without going through useEffect+setState (which the
  // new react-hooks/set-state-in-effect rule rejects).
  const [referralCode] = useState(_readReferralCode);

  const openWaitlist = useCallback((plan) => {
    setWaitlistPlan(plan);
    setWaitlistOpen(true);
    trackLandingCtaClicked({ cta_name: `waitlist_${plan}`, section: "pricing" });
  }, []);

  useEffect(() => {
    trackLandingV2Viewed();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-v2">
      <PromoTicker />
      <ReferralBanner code={referralCode} />
      <TopNav />
      <Hero />
      <DiscoverySection />
      <HowItWorks />
      <FeatureCategories />
      <GlobalSection />
      <PartnershipSection />
      <PortfolioSection />
      <Testimonials />
      <PricingSection onJoinWaitlist={openWaitlist} />
      <FinalCTA />
      <Footer />

      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        plan={waitlistPlan}
      />
    </div>
  );
}
